import apiClient from '../services/apiClient';

/** A refusal the share UI shows as written: `userMessage`, with the backend's status and code. */
export class ShareCallError extends Error {
  userMessage?: string;
  status?: number;
  code?: string;
}

function messageOf(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const m = b['message'] ?? b['error'];
  if (typeof m === 'string') return m;
  if (m && typeof m === 'object' && typeof (m as { message?: unknown }).message === 'string') return (m as { message: string }).message;
  return null;
}

function codeOf(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const b = body as Record<string, unknown>;
  const c = b['errorCode'] ?? b['code'] ?? (b['error'] && typeof b['error'] === 'object' ? (b['error'] as Record<string, unknown>)['code'] : undefined);
  return typeof c === 'string' ? c : undefined;
}

function refusal(body: unknown, status: number | undefined, fallback: string): ShareCallError {
  const msg = messageOf(body);
  const err = new ShareCallError(msg || fallback);
  // Short, human sentences only: a stack or a Prisma message is not for people.
  if (msg && msg.length < 200 && !/prisma|exception|undefined|\bnull\b/i.test(msg)) err.userMessage = msg;
  err.status = status ?? (body && typeof body === 'object' ? Number((body as Record<string, unknown>)['statusCode']) || undefined : undefined);
  err.code = codeOf(body);
  return err;
}

/**
 * One gateway call for the share UI — POST `/<service>/<action>` through the app's
 * apiClient (Clerk token when signed in, 401 → sign-in), the `{ success, data }`
 * envelope unwrapped, and any refusal thrown with the backend's sentence.
 *
 * Every share call in Artwork (the popup, the invite sheet, the view page) goes
 * through here, so an error reads the same wherever it shows.
 */
export async function gatewayCall<T = unknown>(
  service: string,
  action: string,
  data: Record<string, unknown> = {},
  opts: { signal?: AbortSignal; method?: 'get' | 'post' | 'patch' | 'delete' } = {},
): Promise<T> {
  let body: unknown;
  try {
    // The shared bell reads with GET (data as the query) and marks read with PATCH.
    const method = opts.method ?? 'post';
    const signal = opts.signal ? { signal: opts.signal } : {};
    const res =
      method === 'post'
        ? await apiClient.post(`/${service}/${action}`, data, signal)
        : await apiClient.request({ url: `/${service}/${action}`, method, ...(method === 'get' ? { params: data } : { data }), ...signal });
    body = res?.data;
  } catch (err) {
    const e = err as { name?: string; response?: { status?: number; data?: unknown } };
    if (e?.name === 'CanceledError' || e?.name === 'AbortError') throw err;
    throw refusal(e?.response?.data, e?.response?.status, "Couldn't reach Flobi. Check your connection and try again.");
  }
  if (body && typeof body === 'object' && (body as { success?: unknown }).success === false) {
    throw refusal(body, undefined, 'Request failed');
  }
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body && (body as { success?: unknown }).success === true) {
    return (body as { data: T }).data;
  }
  return body as T;
}
